import React, { useState, useEffect } from "react";
import RetreatApi from "../../api/retreatApi";
import QuickReportApi from "../../api/QuickReportApi";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import Helpers from "../../components/Helpers";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  Ban,
  Briefcase,
  CalendarDays,
  ChevronRight,
  Crown,
  Mic,
  MicOff,
  UserCheck,
  Users,
  UserX,
} from "lucide-react";
import "../../assets/css/QuickReport.css";

const REPORT_CARDS = [
  {
    key: "full-list",
    title: "Full List",
    description: "Open the complete couples directory.",
    icon: Users,
    tone: "navy",
    to: "/manageCouples",
  },
  {
    key: "last-2-years",
    title: "Participated in Last 2 Years",
    description: "Couples who attended a Retreat in the last two years.",
    icon: CalendarDays,
    tone: "sky",
    reportId: 6,
    isNotHeadRole: "",
  },
  {
    key: "served-on-team",
    title: "Served on Team",
    description: "Couples who have served on a Retreat team.",
    icon: UserCheck,
    tone: "green",
    reportId: 1,
    isNotHeadRole: "",
  },
  {
    key: "never-served-on-team",
    title: "Never Served on Team",
    description: "Couples who have not yet served on a team.",
    icon: UserX,
    tone: "amber",
    reportId: 2,
    isNotHeadRole: "",
  },
  {
    key: "retreat-lead",
    title: "Served as a Retreat Lead Couple",
    description: "Couples who have served as Retreat lead.",
    icon: Crown,
    tone: "purple",
    reportId: 8,
    isNotHeadRole: "",
  },
  {
    key: "head-of-service",
    title: "Served as a Head of Service",
    description: "Couples who have served as a head of service.",
    icon: Briefcase,
    tone: "gold",
    reportId: 9,
    isNotHeadRole: "",
  },
  {
    key: "never-head-of-service",
    title: "Never Served as a Head of Service",
    description: "Couples who have not served as a head of service.",
    icon: Ban,
    tone: "burgundy",
    reportId: 10,
    isNotHeadRole: "isNotHeadRole",
  },
  {
    key: "given-a-talk",
    title: "Given a Talk",
    description: "Couples who have given a Retreat talk.",
    icon: Mic,
    tone: "teal",
    reportId: 3,
    isNotHeadRole: "",
  },
  {
    key: "never-given-a-talk",
    title: "Never Given a Talk",
    description: "Couples who have not given a talk.",
    icon: MicOff,
    tone: "navy",
    reportId: 4,
    isNotHeadRole: "",
  },
];

const QuickReport = () => {
  let retreatApi = new RetreatApi();
  let quickReportApi = new QuickReportApi();
  let navigate = useNavigate();
  const [allRetreatData, setAllRetreatData] = useState([]);
  const [allSearchCounts, setallSearchCounts] = useState([]);

  const searchInitial = {
    retreatId: 0,
    dateFrom: "",
    dateTo: "",
  };

  Object.freeze(searchInitial);
  const [searchQuery, setSearchQuery] = useState(searchInitial);

  useEffect(() => {
    getAllRetreats();
    searchCounts();
  }, []);

  const handleSearchChanges = (event, id = null) => {
    let { name, value } = event.target;
    const search = { ...searchQuery };
    search.retreatId = value;
    setSearchQuery(search);
    searchCounts(search);
  };

  const searchCounts = (search) => {
    if (search && search != null) {
      quickReportApi
        .searchCounts(search)
        .then((res) => {
          if (res.success) {
            setallSearchCounts(res.retreats);
          } else {
            toast.error(res.msg);
          }
        })
        .catch((error) => {
          toast.error(error);
        });
    } else {
      quickReportApi
        .searchCounts(searchQuery)
        .then((res) => {
          if (res.success) {
            setallSearchCounts(res.retreats);
          } else {
            toast.error(res.msg);
          }
        })
        .catch((error) => {
          toast.error(error);
        });
    }
  };

  const getAllRetreats = async (
    pageNo = 1,
    size = 15,
    searchKeyword = null,
    sortBy = {},
    loadCount = false
  ) => {
    let d = {};
    if (pageNo && size) {
      d.pageNo = pageNo;
      d.size = size;
    }
    if (sortBy.sortColumn) {
      d.sortBy = sortBy;
    }
    d.allRetreat = true;
    await retreatApi
      .getAllRetreats(d)
      .then((res) => {
        if (res.success) {
          let totalRecords = res.totalRecords ? res.totalRecords : 0;
          setAllRetreatData(res.data);
        } else {
          toast.error(res.msg);
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  const toSearchData = (reportId, isNotHeadRole) => {
    navigate(`/search_data`, {
      state: {
        filter: searchQuery,
        reportId: reportId,
        is_Not_Head_Role: isNotHeadRole,
      },
    });
  };

  const renderCardContent = (card) => {
    const Icon = card.icon;
    return (
      <>
        <span className="qr-card-icon" aria-hidden="true">
          <Icon />
        </span>
        <span className="qr-card-body">
          <span className="qr-card-title">{card.title}</span>
          <span className="qr-card-desc">{card.description}</span>
          <span className="qr-card-cta">
            Open
            <ChevronRight />
          </span>
        </span>
        <span className="qr-card-chevron" aria-hidden="true">
          <ChevronRight />
        </span>
      </>
    );
  };

  return (
    <div className="qr-page">
      <header className="qr-header">
        <div>
          <p className="qr-kicker">Reports</p>
          <div className="qr-title-row">
            <h1 className="qr-title">Quick Reports</h1>
            <span className="qr-count">{REPORT_CARDS.length}</span>
          </div>
          <p className="qr-lead">
            Open a saved view of Couples by Retreat Service, Talks, and Team
            history.
          </p>
        </div>
      </header>

      <div className="qr-grid">
        {REPORT_CARDS.map((card) =>
          card.to ? (
            <Link
              key={card.key}
              to={card.to}
              className={`qr-card qr-card--${card.tone}`}
              aria-label={`${card.title}. ${card.description}`}
            >
              {renderCardContent(card)}
            </Link>
          ) : (
            <button
              key={card.key}
              type="button"
              className={`qr-card qr-card--${card.tone}`}
              aria-label={`${card.title}. ${card.description}`}
              onClick={() => toSearchData(card.reportId, card.isNotHeadRole)}
            >
              {renderCardContent(card)}
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default QuickReport;
